import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { TechnicianAutocompleteResult, SearchOrAddTechnicianService, TechnicianProviderOption } from 'src/app/services/modals/search-or-add-technician.service';
import { Observable, of, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, catchError, tap, map } from 'rxjs/operators';
import { Technician } from '../select-or-add-modal/select-or-add-modal.interfaces';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-search-or-add-technician',
  templateUrl: './search-or-add-technician.component.html',
  styleUrls: ['./search-or-add-technician.component.scss']
})
export class SearchOrAddTechnicianComponent {
  form: FormGroup = this.fb.group({
    first_name: ['', Validators.required],
    last_name: ['', Validators.required],
    email: ['', Validators.required],
    timezone: [''],
    phone: [''],
    city: [''],
    region: [''],
    country: ['', Validators.required],
    technician_provider: [null as string | TechnicianProviderOption | null]
  })
  selectedTechnicianUuid: string = '';
  results$!: Observable<TechnicianAutocompleteResult[]>;
  providerResults$!: Observable<TechnicianProviderOption[]>;

  constructor(
    public dialogRef: MatDialogRef<SearchOrAddTechnicianComponent>,
    private fb: FormBuilder,
    private ser: SearchOrAddTechnicianService,
    @Inject(MAT_DIALOG_DATA) public data: {ladingUuid?: string, action: string, technicianUuid?: string},
    private toastr: ToastrService
  ){
    console.log('Technician Component loaded with lading uuid ', this.data.ladingUuid);
  }

  ngOnInit(){
    // Add technician autocomplete feature for when modal needs to be able to select one
    if(['update_lading', 'edit_technician', 'delete_technician'].includes(this.data.action)){
      /* This part allows autocomplete for either first or last name */
      const firstNameCtrl = this.form.get('first_name');
      const lastNameCtrl  = this.form.get('last_name');

      const streams = [];

      // Check for existences of controls because TS is dumb
      if (firstNameCtrl) {
        streams.push(
          firstNameCtrl.valueChanges.pipe(
            map(v => ({ type: 'first_name' as const, v }))
          )
        );
      }
      if (lastNameCtrl) {
        streams.push(
          lastNameCtrl.valueChanges.pipe(
            map(v => ({ type: 'last_name' as const, v }))
          )
        );
      }
      if (streams.length === 0) return;

      // Autocomplete magic happens here
      this.results$ = merge(...streams).pipe(
        debounceTime(250),
        distinctUntilChanged((a, b) => a.type === b.type && a.v === b.v),
        filter(({ v }) => typeof v === 'string' && v.trim().length >= 2),
        switchMap(({ type, v }) =>
          this.ser.search(type, v.trim()).pipe(
            catchError(err => {
              console.error('autocomplete HTTP error:', err);
              return of([]);
            })
          )
        )
      );
    }

    /* Add autocomplete for technician provider when user needs to add or edit a technician */
    if(['update_lading','add_technician','edit_technician'].includes(this.data.action)){
      const provider = this.form.get('technician_provider');
      if (!provider){
        return;
      } 
      this.providerResults$ = provider.valueChanges.pipe(
        debounceTime(250),
        distinctUntilChanged(),
        filter(v => typeof v === 'string' && v.trim().length >= 2),
        switchMap(v => 
          this.ser.searchProviders(v.trim()).pipe(
            catchError(err => {
              console.error('Provider autocomplete HTTP error:', err);
              return of([]);
            })
          )
        )
      )
    }
  }
  
  // Controls what text shows in the input when a mat-option object is selected
  displayTechnician = (v: TechnicianAutocompleteResult | string | null): string => {
    if (!v) return '';
    if (typeof v === 'string') return v;
    return `${v.first_name} ${v.last_name}`;
  };

  // decide how to display technician provider to user
  displayProvider(p: TechnicianProviderOption | string | null): string{
    if (typeof p === 'object' && p)
      return p.name;
    if (p)
      return p;
    return '';
  }

  onTechnicianSelected(t: TechnicianAutocompleteResult) {
    this.selectedTechnicianUuid = t.technician_uuid;
        this.form.patchValue({
        first_name: t.first_name, 
        last_name: t.last_name,
        email: t.email,
        phone: t.phone,
        timezone: t.timezone,
        city: t.city,
        region: t.region,
        country: t.country,
        technician_provider: t.technician_provider_uuid && t.technician_provider_name ? { technician_provider_uuid: t.technician_provider_uuid, name: t.technician_provider_name} : null
      }, { emitEvent: false }
    );
  }

  updateTechnician(){
    if (!this.data.ladingUuid) return;
    console.log('Technician selected, ', this.selectedTechnicianUuid);

    this.ser.updateLadingSiteCheckTechnician(this.data.ladingUuid, this.selectedTechnicianUuid)
    .subscribe({
      next: (res) => {
        console.log(res);
        if(res.success){
          this.toastr.success('Technician successfully updated.');
          this.closeModal();
        }
        else{
          this.toastr.error('Technician could not be updated');
          this.closeModal();
        }
      }, 
      error: (err) => {
        console.error('Http error: ', err);
        this.toastr.error('Technician could not be updated.');
      }
    })
  }

  addNewTechnician(){
    // Check for form validity
    if(this.form.invalid){
      this.form.markAllAsTouched();
      this.toastr.error('Please fill in required fields before submitting a new technician.');
      return;
    }

    const payload = this.form.value;

    // if technician provider was added, remove all extra technician provider data, only keep uuid
    if (payload.technician_provider !== null && payload.technician_provider.technician_provider_uuid && payload.technician_provider.technician_provider_uuid.length > 0){
      payload['technician_provider_uuid'] = payload.technician_provider.technician_provider_uuid;
      delete payload.technician_provider;
    }
    // remove unfilled data from payload, because they're not null, they're empty strings
    for (const [key, value] of Object.entries(payload)){
      if (typeof value === 'string' && value.length == 0)
        delete payload[key];
    }
    //console.log('payload', payload);

    const backendAction = this.data.action === 'update_lading' ? 'add_technician_update_lading' : 'add_technician_only';

    this.ser.addNewTechnician(this.form.value, backendAction).subscribe({
      next: (res) => {
        //console.log(res);
        if(res.success){
          let successMsg: string;
          if (backendAction === 'add_technician_only'){
            successMsg = 'Technician has been added.';
          }
          else{
            successMsg = 'Technician added and lading has been updated.';
          }
          this.toastr.success(successMsg);
          this.closeModal();
        }
        else{
          console.warn('Response received but not successful: ', res)
          this.toastr.error('Technician could not be added.');
        }    
      },
      error: (err) => {
        console.error('Http error: ', err);
        this.toastr.error('Technician could not be added.');
      }
    })
  }

  submit(){
    if (this.data.action === 'update_lading' && this.data.ladingUuid){
      if (this.selectedTechnicianUuid.length > 0) 
        this.updateTechnician();
      else
        this.addNewTechnician();
    }
    else if (this.data.action === 'add_technician'){
      this.addNewTechnician();
    }
  }

  closeModal(){
    this.dialogRef.close()
  }
}
